import { useQuery } from "@apollo/client";
import { Skeleton } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import StatCard from "../../components/StatCard/StatCard";
import { TotalUsersCountryItem } from "../../graphql/graphql";
import { ANALYTICS } from "../../graphql/queries-gql";
import { useChartTheme } from "../../theme/chartTheme";

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  align-items: start;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CountryList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 32px;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
`;

const BreakdownLabel = styled.span`
  color: ${(props) => props.theme.secondaryTextColor};
`;

const BreakdownValue = styled.span`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const BreakdownPercent = styled.span`
  display: inline-block;
  min-width: 44px;
  margin-left: 8px;
  text-align: right;
  font-weight: 400;
  color: ${(props) => props.theme.mutedTextColor};
`;

const CHART_HEIGHT = 260;

const Analytics = () => {
  const chart = useChartTheme();
  const { data, loading, error } = useQuery(ANALYTICS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("onCompleted", data);
    },
    onError: (error) => {
      console.log("onError", error);
    },
  });

  if (loading) {
    return (
      <Page title="Analytics">
        <SummaryGrid>
          <Skeleton variant="rounded" height={96} />
        </SummaryGrid>
        <ChartGrid>
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={CHART_HEIGHT + 60} />
          ))}
        </ChartGrid>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Analytics">
        <ErrorAlert error={error} />
      </Page>
    );
  }

  if (!data) {
  }

  const analytics = data.analytics;

  const percentOfVerified = (value: number) =>
    Math.round((value / analytics.totalVerifiedUsers) * 100);

  const renderRow = (label: string, value: number) => (
    <BreakdownRow key={label}>
      <BreakdownLabel>{label}</BreakdownLabel>
      <BreakdownValue>
        {value}
        <BreakdownPercent>{percentOfVerified(value)}%</BreakdownPercent>
      </BreakdownValue>
    </BreakdownRow>
  );

  const renderBarChart = (
    rows: { date: string }[],
    dataKey: string,
    color: string,
  ) => (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={rows}>
        <CartesianGrid {...chart.grid} />
        {/* Dates are only used as the tooltip label */}
        <XAxis dataKey="date" hide />
        <YAxis {...chart.axis} width={48} />
        <Tooltip {...chart.tooltip} />
        <Bar dataKey={dataKey} fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <Page title="Analytics" maxWidth={1280}>
      <SummaryGrid>
        <StatCard
          label="Total verified users"
          value={analytics.totalVerifiedUsers}
        />
        <StatCard
          label="New users last 30 days"
          value={analytics.totalNewUsersLast30days}
        >
          <BreakdownRow>
            <BreakdownLabel>Share of verified users</BreakdownLabel>
            <BreakdownValue>
              {percentOfVerified(analytics.totalNewUsersLast30days)}%
            </BreakdownValue>
          </BreakdownRow>
        </StatCard>
      </SummaryGrid>

      <ChartGrid>
        <Panel title="Daily active users, 90 days">
          {renderBarChart(analytics.daus, "users", chart.colors[0])}
        </Panel>
        <Panel title="Monthly active users, 90 days">
          {renderBarChart(analytics.daus, "mau", chart.colors[4])}
        </Panel>
        <Panel title="Total users, 12 months">
          {renderBarChart(analytics.totalUsers, "users", chart.colors[1])}
        </Panel>
      </ChartGrid>

      <SummaryGrid>
        <Panel title="Gender distribution">
          <BreakdownList>
            {renderRow("Female", analytics.totalFemaleUsers)}
            {renderRow("Male", analytics.totalMaleUsers)}
            {renderRow("Other", analytics.totalOtherGenderUsers)}
          </BreakdownList>
        </Panel>

        <Panel title="Store data">
          <BreakdownList>
            {renderRow("Premium", analytics.totalUsersWithPremium)}
            {renderRow("Paying", analytics.totalPayingUsers)}
          </BreakdownList>
        </Panel>

        <Panel title="Activity">
          <BreakdownList>
            {renderRow("Last 30 days", analytics.totalActiveUsersLast30days)}
            {renderRow(
              "Paying last 30 days",
              analytics.totalPayingUsersLast30days,
            )}
            {renderRow(
              "Active last 30 (created 90 days ago)",
              analytics.totalActiveUsersLast30daysCreatedMoreThan90daysAgo,
            )}
          </BreakdownList>
        </Panel>

        <Panel title="Platform distribution">
          <BreakdownList>
            {renderRow("iOS", analytics.totalUsersPlatformIos)}
            {renderRow("Android", analytics.totalUsersPlatformAndroid)}
            {renderRow("Web", analytics.totalUsersPlatformWeb)}
          </BreakdownList>
        </Panel>

        <Panel title="Auth distribution">
          <BreakdownList>
            {renderRow("Apple", analytics.totalUsersLoginApple)}
            {renderRow("Google", analytics.totalUsersLoginGoogle)}
            {renderRow("Facebook", analytics.totalUsersLoginFacebook)}
            {renderRow("Email", analytics.totalUsersLoginEmail)}
          </BreakdownList>
        </Panel>
      </SummaryGrid>

      <Panel title="Country distribution">
        <CountryList>
          {analytics.totalUsersByCountry.map((item: TotalUsersCountryItem) => (
            <BreakdownRow key={item.country}>
              <BreakdownLabel>{item.country}</BreakdownLabel>
              <BreakdownValue>
                {item.users}
                <BreakdownPercent>
                  {Math.round(
                    (item.users / analytics.totalVerifiedUsers) * 100,
                  )}
                  %
                </BreakdownPercent>
              </BreakdownValue>
            </BreakdownRow>
          ))}
        </CountryList>
      </Panel>
    </Page>
  );
};
export default Analytics;
